import { useEffect } from "react";

interface Palette {
  accent: string;       // overrides --tg-theme-accent-text-color
  button: string;       // overrides --tg-theme-button-color
  buttonText: string;   // overrides --tg-theme-button-text-color (kept white)
  glow: string;         // boxShadow-friendly rgba for elemental halos
  link: string;         // overrides --tg-theme-link-color
}

const Palettes: Record<number, Palette> = {
  0: {
    accent: "#ff8a4d",
    button: "#d94326",
    buttonText: "#ffffff",
    glow: "rgba(255, 120, 60, 0.45)",
    link: "#ff8a4d",
  },
  1: {
    accent: "#5ac8ff",
    button: "#2e7dd9",
    buttonText: "#ffffff",
    glow: "rgba(70, 170, 255, 0.45)",
    link: "#5ac8ff",
  },
  2: {
    accent: "#b48dff",
    button: "#7c4dff",
    buttonText: "#ffffff",
    glow: "rgba(180, 140, 255, 0.45)",
    link: "#b48dff",
  },
  3: {
    accent: "#7ed46d",
    button: "#3f9c3f",
    buttonText: "#ffffff",
    glow: "rgba(120, 200, 110, 0.45)",
    link: "#7ed46d",
  },
};

// Light-theme background palette per element. In light Telegram the SDK gives
// near-white bg + light-gray secondary-bg, which reads as bland and washed-out
// next to our painterly assets. We override those two vars with a soft tint of
// the player's element so page and cards feel "fire-ish" / "water-ish" etc.
// Tints are intentionally mild (≈10–15% saturation) so dark text stays readable.
interface LightTint {
  bg: string;          // overrides --tg-theme-bg-color (page background)
  secondaryBg: string; // overrides --tg-theme-secondary-bg-color (cards)
  headerBg: string;    // overrides --tg-theme-header-bg-color (top bar / nav)
  sectionBg: string;   // overrides --tg-theme-section-bg-color
}

const LightTints: Record<number, LightTint> = {
  0: { // Fire — peach / cream
    bg:          "#fff5ec",
    secondaryBg: "#ffe2c8",
    headerBg:    "#ffead7",
    sectionBg:   "#ffefe0",
  },
  1: { // Water — soft blue
    bg:          "#eef6ff",
    secondaryBg: "#d3e6f7",
    headerBg:    "#dceefb",
    sectionBg:   "#e3eff8",
  },
  2: { // Air — lavender
    bg:          "#f4eefd",
    secondaryBg: "#e0d2f3",
    headerBg:    "#e9defa",
    sectionBg:   "#ede2f9",
  },
  3: { // Earth — soft sage
    bg:          "#f0f6ec",
    secondaryBg: "#d4e7cb",
    headerBg:    "#dcefd5",
    sectionBg:   "#e3efdb",
  },
};

const Vars = {
  accent: "--tg-theme-accent-text-color",
  button: "--tg-theme-button-color",
  buttonText: "--tg-theme-button-text-color",
  link: "--tg-theme-link-color",
  glow: "--element-glow",
  bg: "--tg-theme-bg-color",
  secondaryBg: "--tg-theme-secondary-bg-color",
  headerBg: "--tg-theme-header-bg-color",
  sectionBg: "--tg-theme-section-bg-color",
};

// Pushes a player's element palette into CSS custom properties on the root.
// Every Tailwind class like `text-tg-accent` / `bg-tg-button` automatically
// repaints because tailwind.config.ts maps those colors to the CSS vars.
// `--element-glow` is a custom var available for elemental halo / glow CSS.
//
// In LIGHT theme we additionally override the bg / secondaryBg / headerBg /
// sectionBg vars with a soft element-tinted palette (LightTints) — otherwise
// the SDK's white-on-light-gray feels disconnected from the painterly
// element-themed accents elsewhere on the screen. In DARK theme we clear
// those overrides so the SDK's dark palette flows through.
//
// We also watch <html data-theme> for changes (user toggles Telegram theme
// mid-session) and re-apply the appropriate tint without a reload.
export function useElementTheme(element: number | undefined) {
  useEffect(() => {
    if (element == null) return;
    const palette = Palettes[element];
    if (!palette) return;
    const root = document.documentElement;
    root.style.setProperty(Vars.accent, palette.accent);
    root.style.setProperty(Vars.button, palette.button);
    root.style.setProperty(Vars.buttonText, palette.buttonText);
    root.style.setProperty(Vars.link, palette.link);
    root.style.setProperty(Vars.glow, palette.glow);

    applyLightTint(element);

    // React to Telegram theme switches (themeChanged event toggles <html
    // data-theme>). MutationObserver is cheaper than a re-render trigger.
    const observer = new MutationObserver(() => applyLightTint(element));
    observer.observe(root, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, [element]);
}

function applyLightTint(element: number) {
  const root = document.documentElement;
  if (root.getAttribute("data-theme") === "light") {
    const tint = LightTints[element];
    if (!tint) return;
    root.style.setProperty(Vars.bg, tint.bg);
    root.style.setProperty(Vars.secondaryBg, tint.secondaryBg);
    root.style.setProperty(Vars.headerBg, tint.headerBg);
    root.style.setProperty(Vars.sectionBg, tint.sectionBg);
  } else {
    // Dark theme — drop our overrides so Telegram's dark palette wins.
    root.style.removeProperty(Vars.bg);
    root.style.removeProperty(Vars.secondaryBg);
    root.style.removeProperty(Vars.headerBg);
    root.style.removeProperty(Vars.sectionBg);
  }
}

// CSS variable reference for inline-style glows.
export const ElementGlowVar = `var(${Vars.glow})`;
